/**
 * Borne de période stockée → `YYYY-MM-DD` pour un `<input type="date">`.
 *
 * Accepte l'ISO (`2026-09-10T00:00:00`) et le date-math Elasticsearch relatif
 * à maintenant (`now`, `now-30d`, `now-1M`, `now-7d/d`), que les pages posent
 * en défaut parce qu'un YAML n'a pas d'horloge. Sans ce décodage, l'input
 * restait VIDE alors que la requête filtrait bien sur 30 jours : l'écran
 * annonçait « toutes les dates » en en montrant un mois.
 *
 * @param {string} value
 *   La borne telle que le magasin la tient.
 * @param {Date} now
 *   L'instant de référence (injectable pour les tests).
 * @return {string}
 *   La date locale, ou '' pour une valeur illisible.
 */
export function boundToYmd(value, now = new Date()) {
  if (typeof value !== 'string') return ''
  if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10)

  const m = /^now(?:([+-])(\d+)([dwMy]))?(?:\/[dwMy])?$/.exec(value.trim())
  if (!m) return ''

  const d = new Date(now)
  if (m[1]) {
    const n = Number(m[2]) * (m[1] === '-' ? -1 : 1)
    if (m[3] === 'd') d.setDate(d.getDate() + n)
    else if (m[3] === 'w') d.setDate(d.getDate() + 7 * n)
    else if (m[3] === 'M') d.setMonth(d.getMonth() + n)
    else d.setFullYear(d.getFullYear() + n)
  }
  const pad = (x) => String(x).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/**
 * Une borne est-elle une date complète (`YYYY-MM-DD`) et réelle ?
 *
 * Un `<input type="date">` rend une valeur VIDE tant que le jour, le mois et
 * l'année ne sont pas tous saisis : une date tapée au clavier passe donc par
 * cet état à chaque frappe. Ce qui en sort n'est pas une période, c'est une
 * période en cours d'écriture — d'où ce garde, avant d'écrire quoi que ce soit
 * dans le magasin.
 *
 * @param {*} value
 *   La valeur rendue par l'input.
 * @return {boolean}
 *   Vrai pour une date complète et valide.
 */
export function isYmd(value) {
  return typeof value === 'string'
    && /^\d{4}-\d{2}-\d{2}$/.test(value)
    && !Number.isNaN(Date.parse(`${value}T00:00:00`))
}

/**
 * Pas de temps d'histogramme déduit de l'étendue d'une période.
 *
 * Premier seuil atteint gagne : des paquets fins sur une courte période, plus
 * larges sur une longue.
 *
 * @param {Date} fromDate
 *   Début de la période.
 * @param {Date} toDate
 *   Fin de la période.
 * @param {Array<{maxDays: number, interval: string}>} breakpoints
 *   Les seuils, du plus fin au plus large.
 * @return {string}
 *   Le `fixed_interval` Elasticsearch (`15m`, `1h`, …).
 */
export function pickInterval(fromDate, toDate, breakpoints) {
  const days = (toDate.getTime() - fromDate.getTime()) / 86_400_000
  // Une borne illisible donne NaN, qui est faux face à CHAQUE seuil : la
  // boucle tombait alors sur le dernier, le plus large — « 7 jours » affichés
  // sur une fenêtre d'un jour. Sans étendue lisible, on garde le pas le plus
  // fin : il peut être trop détaillé, jamais trop grossier.
  if (!Number.isFinite(days)) return breakpoints[0]?.interval ?? '1d'
  for (const bp of breakpoints) {
    if (days <= bp.maxDays) return bp.interval
  }
  return breakpoints[breakpoints.length - 1]?.interval ?? '1d'
}
